import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from '../../components/ui';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Users,
  MessageSquare,
  Phone,
  Mail,
  Linkedin,
  Clock,
  Target,
  Heart,
  AlertTriangle,
  Star,
  Calendar,
  Filter,
  Download,
  RefreshCw
} from 'lucide-react';
import { format, startOfDay, endOfDay, subDays, subMonths } from 'date-fns';

// ============================================================================
// INTERFACES AND TYPES
// ============================================================================

interface ContactEngagementOverview {
  contactId: string;
  overallScore: number;
  channelScores: {
    email: number;
    sms: number;
    linkedin: number;
    calls: number;
    voicemail: number;
  };
  interactionCounts: {
    total: number;
    email: number;
    sms: number;
    linkedin: number;
    calls: number;
    voicemail: number;
  };
  responseMetrics: {
    totalResponses: number;
    averageResponseTime: number;
    fastestResponse: number;
    slowestResponse: number;
  };
  behavioral: {
    preferredChannel: string;
    bestTimeToContact: object;
    bestDayToContact: string;
    timeZone?: string;
  };
  sentiment: {
    positiveCount: number;
    negativeCount: number;
    neutralCount: number;
    averageScore: number;
  };
  conversion: {
    totalConversions: number;
    conversionRate: number;
    averageTimeToConversion: number;
  };
}

interface TimelineEvent {
  id: string;
  timestamp: Date;
  channelType: 'email' | 'sms' | 'linkedin' | 'calls' | 'voicemail';
  eventType: string;
  eventDetails: object;
  location?: string;
  deviceType?: string;
}

interface SentimentAnalysis {
  sentimentScore: number;
  sentimentLabel: 'very_negative' | 'negative' | 'neutral' | 'positive' | 'very_positive';
  confidence: number;
  dominantEmotion: string;
  intent: string;
  keyTopics: string[];
}

interface PredictiveScore {
  engagementPredictionScore: number;
  conversionProbability: number;
  responseProbability: number;
  churnRisk: number;
  leadScore: number;
  predictedBestChannel: string;
  predictedBestTime: string;
  topInfluencingFactors: string[];
}

interface HeatmapData {
  hour: number;
  day: string;
  value: number;
  quality: number;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface ContactAnalyticsDashboardProps {
  contactId: string;
  contactName?: string;
  companyName?: string;
  onContactChange?: (contactId: string) => void;
}

export const ContactAnalyticsDashboard: React.FC<ContactAnalyticsDashboardProps> = ({
  contactId,
  contactName = 'Unknown Contact',
  companyName,
  onContactChange
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('30d');
  const [channelFilter, setChannelFilter] = useState('all');

  // Data state
  const [engagementOverview, setEngagementOverview] = useState<ContactEngagementOverview | null>(null);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [sentimentData, setSentimentData] = useState<SentimentAnalysis[]>([]);
  const [predictiveScores, setPredictiveScores] = useState<PredictiveScore | null>(null);
  const [heatmapData, setHeatmapData] = useState<HeatmapData[]>([]);
  const [trends, setTrends] = useState<any>(null);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchContactAnalytics = async () => {
    try {
      setLoading(true);
      
      // Try to fetch real data, but fall back to mock data if API is not available
      try {
        // Fetch engagement overview
        const overviewResponse = await fetch(`/api/analytics/contacts/${contactId}/overview`);
        if (overviewResponse.ok) {
          const overviewData = await overviewResponse.json();
          setEngagementOverview(overviewData.data);
        } else {
          throw new Error('API not available');
        }

        // Fetch timeline events
        const timelineResponse = await fetch(
          `/api/analytics/contacts/${contactId}/timeline?limit=100&${getTimeRangeParams()}`
        );
        if (timelineResponse.ok) {
          const timelineData = await timelineResponse.json();
          setTimelineEvents(timelineData.data.timeline || []);
        }

        // Fetch sentiment data
        const sentimentResponse = await fetch(`/api/analytics/contacts/${contactId}/sentiment`);
        if (sentimentResponse.ok) {
          const sentimentData = await sentimentResponse.json();
          setSentimentData(sentimentData.data || []);
        }

        // Fetch predictive scores
        const predictiveResponse = await fetch(`/api/analytics/contacts/${contactId}/predictive`);
        if (predictiveResponse.ok) {
          const predictiveData = await predictiveResponse.json();
          setPredictiveScores(predictiveData.data);
        }
      } catch (apiError) {
        console.log('API not available, using mock data for demo');
        
        // Mock data for demonstration
        setEngagementOverview({
          contactId,
          overallScore: 78.5,
          channelScores: {
            email: 82.3,
            sms: 75.1,
            linkedin: 68.9,
            calls: 85.2,
            voicemail: 72.4
          },
          interactionCounts: {
            total: 156,
            email: 89,
            sms: 34,
            linkedin: 12,
            calls: 15,
            voicemail: 6
          },
          responseMetrics: {
            totalResponses: 47,
            averageResponseTime: 4.2,
            fastestResponse: 0.5,
            slowestResponse: 24.8
          },
          behavioral: {
            preferredChannel: 'email',
            bestTimeToContact: { hour: 14, day: 'Tuesday' },
            bestDayToContact: 'Tuesday',
            timeZone: 'EST'
          },
          sentiment: {
            positiveCount: 32,
            negativeCount: 8,
            neutralCount: 7,
            averageScore: 0.72
          },
          conversion: {
            totalConversions: 3,
            conversionRate: 6.4,
            averageTimeToConversion: 12.5
          }
        });

        // Mock timeline events
        const mockEvents: TimelineEvent[] = [
          {
            id: '1',
            timestamp: new Date(Date.now() - 1000 * 60 * 30),
            channelType: 'email',
            eventType: 'opened',
            eventDetails: { subject: 'Follow-up on our conversation' }
          },
          {
            id: '2',
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
            channelType: 'email',
            eventType: 'sent',
            eventDetails: { subject: 'Follow-up on our conversation' }
          },
          {
            id: '3',
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
            channelType: 'calls',
            eventType: 'connected',
            eventDetails: { duration: 420 }
          }
        ];
        setTimelineEvents(mockEvents);

        // Mock sentiment data
        setSentimentData([
          {
            sentimentScore: 0.8,
            sentimentLabel: 'positive',
            confidence: 0.92,
            dominantEmotion: 'interested',
            intent: 'purchase_consideration',
            keyTopics: ['pricing', 'features', 'timeline']
          }
        ]);

        // Mock predictive scores
        setPredictiveScores({
          engagementPredictionScore: 82.5,
          conversionProbability: 0.34,
          responseProbability: 0.78,
          churnRisk: 0.12,
          leadScore: 85,
          predictedBestChannel: 'email',
          predictedBestTime: '2:00 PM',
          topInfluencingFactors: ['Previous engagement', 'Response time', 'Content relevance']
        });
      }

      // Generate heatmap data
      generateHeatmapData();

    } catch (error) {
      console.error('Error fetching contact analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchContactAnalytics();
    setRefreshing(false);
  };

  const getTimeRangeParams = () => {
    const endDate = new Date();
    let startDate: Date;

    switch (timeRange) {
      case '7d':
        startDate = subDays(endDate, 7);
        break;
      case '30d':
        startDate = subDays(endDate, 30);
        break;
      case '90d':
        startDate = subDays(endDate, 90);
        break;
      case '1y':
        startDate = subMonths(endDate, 12);
        break;
      default:
        startDate = subDays(endDate, 30);
    }

    return `startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
  };

  // ============================================================================
  // DATA PROCESSING
  // ============================================================================

  const generateHeatmapData = () => {
    if (!timelineEvents.length) return;

    const heatmap: HeatmapData[] = [];
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    for (let day = 0; day < 7; day++) {
      for (let hour = 0; hour < 24; hour++) {
        const dayEvents = timelineEvents.filter(event => {
          const eventDate = new Date(event.timestamp);
          return eventDate.getDay() === day && eventDate.getHours() === hour;
        });

        heatmap.push({
          hour,
          day: days[day],
          value: dayEvents.length,
          quality: calculateEngagementQuality(dayEvents),
        });
      }
    }

    setHeatmapData(heatmap);
  };

  const calculateEngagementQuality = (events: TimelineEvent[]): number => {
    const weights = {
      sent: 1,
      delivered: 2,
      opened: 5,
      clicked: 10,
      replied: 20,
      called: 15,
      connected: 25,
    };

    const totalScore = events.reduce((sum, event) => {
      return sum + (weights[event.eventType as keyof typeof weights] || 1);
    }, 0);

    return events.length > 0 ? Math.min(100, (totalScore / events.length) * 4) : 0;
  };

  const getChannelIcon = (channel: string) => {
    const icons = {
      email: Mail,
      sms: MessageSquare,
      linkedin: Linkedin,
      calls: Phone,
      voicemail: Phone,
    };
    return icons[channel as keyof typeof icons] || Activity;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getSentimentColor = (sentiment: string) => {
    const colors = {
      very_positive: 'bg-green-500',
      positive: 'bg-green-400',
      neutral: 'bg-gray-400',
      negative: 'bg-red-400',
      very_negative: 'bg-red-500',
    };
    return colors[sentiment as keyof typeof colors] || 'bg-gray-400';
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    if (contactId) {
      fetchContactAnalytics();
    }
  }, [contactId, timeRange, channelFilter]);

  // ============================================================================
  // RENDER COMPONENTS
  // ============================================================================

  const renderOverviewTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* Engagement Score Card */}
      <Card className="bg-gradient-to-br from-neutral-800/50 to-neutral-700/30 border-neutral-600/50 hover:border-neutral-500/50 transition-all duration-300">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-neutral-200">Overall Engagement</CardTitle>
          <Activity className="h-4 w-4 text-blue-400" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-white">
            {engagementOverview?.overallScore?.toFixed(1) || '0'}
          </div>
          <Progress 
            value={engagementOverview?.overallScore || 0} 
            className="mt-3 h-2 bg-neutral-700"
          />
          <p className="text-xs text-neutral-400 mt-2">
            Based on {engagementOverview?.interactionCounts?.total || 0} interactions
          </p>
        </CardContent>
      </Card>

      {/* Channel Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Channel Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {engagementOverview && Object.entries(engagementOverview.channelScores).map(([channel, score]) => {
              const Icon = getChannelIcon(channel);
              return (
                <div key={channel} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon className="h-4 w-4" />
                    <span className="text-sm capitalize">{channel}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-medium ${getScoreColor(score)}`}>
                      {score.toFixed(1)}
                    </span>
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${Math.min(100, score)}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Response Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Response Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total Responses</span>
              <span className="font-medium">{engagementOverview?.responseMetrics?.totalResponses || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Avg Response Time</span>
              <span className="font-medium">
                {Math.round(engagementOverview?.responseMetrics?.averageResponseTime || 0)}m
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Fastest Response</span>
              <span className="font-medium">
                {Math.round(engagementOverview?.responseMetrics?.fastestResponse || 0)}m
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sentiment Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Sentiment Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Average Sentiment</span>
              <span className="font-medium">
                {(engagementOverview?.sentiment?.averageScore || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex space-x-1">
              {['very_positive', 'positive', 'neutral', 'negative', 'very_negative'].map(sentiment => (
                <div
                  key={sentiment}
                  className={`h-2 rounded ${getSentimentColor(sentiment)}`}
                  style={{
                    width: `${((engagementOverview?.sentiment as any)?.[sentiment + 'Count'] || 0) * 20}%`
                  }}
                />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="text-center">
                <div className="font-medium text-green-600">
                  {engagementOverview?.sentiment?.positiveCount || 0}
                </div>
                <div className="text-muted-foreground">Positive</div>
              </div>
              <div className="text-center">
                <div className="font-medium text-gray-600">
                  {engagementOverview?.sentiment?.neutralCount || 0}
                </div>
                <div className="text-muted-foreground">Neutral</div>
              </div>
              <div className="text-center">
                <div className="font-medium text-red-600">
                  {engagementOverview?.sentiment?.negativeCount || 0}
                </div>
                <div className="text-muted-foreground">Negative</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversion Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Conversion Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total Conversions</span>
              <span className="font-medium">{engagementOverview?.conversion?.totalConversions || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Conversion Rate</span>
              <span className="font-medium">
                {(engagementOverview?.conversion?.conversionRate || 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Avg Time to Convert</span>
              <span className="font-medium">
                {Math.round(engagementOverview?.conversion?.averageTimeToConversion || 0)}h
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Behavioral Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Behavioral Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-muted-foreground">Preferred Channel</div>
              <div className="flex items-center space-x-2 mt-1">
                {(() => {
                  const Icon = getChannelIcon(engagementOverview?.behavioral?.preferredChannel || 'email');
                  return <Icon className="h-4 w-4" />;
                })()}
                <span className="font-medium capitalize">
                  {engagementOverview?.behavioral?.preferredChannel || 'Email'}
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Best Day</div>
              <div className="font-medium capitalize">
                {engagementOverview?.behavioral?.bestDayToContact || 'Monday'}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Time Zone</div>
              <div className="font-medium">
                {engagementOverview?.behavioral?.timeZone || 'UTC'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderTimelineTab = () => (
    <Card>
      <CardHeader>
        <CardTitle>Engagement Timeline</CardTitle>
        <CardDescription>
          Chronological view of all contact interactions
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {timelineEvents.map((event, index) => {
            const Icon = getChannelIcon(event.channelType);
            return (
              <div key={event.id || index} className="flex items-start space-x-3 p-3 border rounded-lg">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">
                      {event.eventType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                    <p className="text-sm text-gray-500">
                      {format(new Date(event.timestamp), 'MMM d, yyyy HH:mm')}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      {event.channelType}
                    </Badge>
                    {event.location && (
                      <Badge variant="outline" className="text-xs">
                        {event.location}
                      </Badge>
                    )}
                    {event.deviceType && (
                      <Badge variant="outline" className="text-xs">
                        {event.deviceType}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  const renderHeatmapTab = () => (
    <Card>
      <CardHeader>
        <CardTitle>Interaction Heatmap</CardTitle>
        <CardDescription>
          Visual representation of engagement patterns by time
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Heatmap would be implemented with a proper heatmap library */}
          <div className="grid grid-cols-24 gap-1">
            {heatmapData.map((cell, index) => (
              <div
                key={index}
                className="aspect-square rounded-sm"
                style={{
                  backgroundColor: `rgba(59, 130, 246, ${cell.value / 10})`,
                }}
                title={`${cell.day} ${cell.hour}:00 - ${cell.value} interactions`}
              />
            ))}
          </div>
          
          {/* Heatmap Legend */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Less</span>
            <div className="flex space-x-1">
              {[0, 0.2, 0.4, 0.6, 0.8, 1].map(opacity => (
                <div
                  key={opacity}
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: `rgba(59, 130, 246, ${opacity})` }}
                />
              ))}
            </div>
            <span>More</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderPredictiveTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Engagement Prediction */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Engagement Prediction</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {predictiveScores?.engagementPredictionScore || 0}%
          </div>
          <Progress value={predictiveScores?.engagementPredictionScore || 0} className="mt-2" />
          <p className="text-xs text-muted-foreground mt-2">
            Likelihood of future engagement
          </p>
        </CardContent>
      </Card>

      {/* Conversion Probability */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Conversion Probability</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {((predictiveScores?.conversionProbability || 0) * 100).toFixed(1)}%
          </div>
          <Progress value={(predictiveScores?.conversionProbability || 0) * 100} className="mt-2" />
          <p className="text-xs text-muted-foreground mt-2">
            Likelihood of conversion
          </p>
        </CardContent>
      </Card>

      {/* Churn Risk */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Churn Risk</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-red-600">
            {((predictiveScores?.churnRisk || 0) * 100).toFixed(1)}%
          </div>
          <Progress 
            value={(predictiveScores?.churnRisk || 0) * 100} 
            className="mt-2"
          />
          <p className="text-xs text-muted-foreground mt-2">
            Risk of disengagement
          </p>
        </CardContent>
      </Card>

      {/* Lead Score */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Lead Score</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {predictiveScores?.leadScore || 0}
          </div>
          <Progress value={predictiveScores?.leadScore || 0} className="mt-2" />
          <p className="text-xs text-muted-foreground mt-2">
            Overall lead quality
          </p>
        </CardContent>
      </Card>

      {/* Predictions Card */}
      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>AI Predictions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Best Channel</div>
              <div className="flex items-center space-x-2 mt-1">
                {(() => {
                  const Icon = getChannelIcon(predictiveScores?.predictedBestChannel || 'email');
                  return <Icon className="h-4 w-4" />;
                })()}
                <span className="font-medium capitalize">
                  {predictiveScores?.predictedBestChannel || 'Email'}
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Best Time</div>
              <div className="font-medium">
                {predictiveScores?.predictedBestTime || '09:00'}
              </div>
            </div>
          </div>
          
          {predictiveScores?.topInfluencingFactors && (
            <div className="mt-4">
              <div className="text-sm text-muted-foreground mb-2">Top Influencing Factors</div>
              <div className="flex flex-wrap gap-2">
                {predictiveScores.topInfluencingFactors.map((factor, index) => (
                  <Badge key={index} variant="secondary">
                    {factor}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            {contactName}
          </h1>
          {companyName && (
            <p className="text-lg text-neutral-400 mt-1">{companyName}</p>
          )}
          <div className="flex items-center space-x-4 mt-2">
            <Badge variant="secondary" className="bg-blue-500/10 text-blue-400 border-blue-500/20">
              Contact ID: {contactId}
            </Badge>
            {engagementOverview && (
              <Badge 
                variant="secondary" 
                className={`${getScoreColor(engagementOverview.overallScore)} border-current/20`}
              >
                Score: {engagementOverview.overallScore.toFixed(1)}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1">
            {['7d', '30d', '90d', '1y'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range as any)}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  timeRange === range
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={refreshData}
            disabled={refreshing}
            className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Main Analytics Tabs */}
      <div className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 shadow-xl">
        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className="bg-neutral-800/50 border-neutral-700/50">
            <TabsTrigger 
              value="overview"
              className="data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <Activity className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger 
              value="timeline"
              className="data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <Clock className="w-4 h-4 mr-2" />
              Timeline
            </TabsTrigger>
            <TabsTrigger 
              value="heatmap"
              className="data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Heatmap
            </TabsTrigger>
            <TabsTrigger 
              value="predictive"
              className="data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Predictive
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-8">
            {renderOverviewTab()}
          </TabsContent>

          <TabsContent value="timeline" className="mt-8">
            {renderTimelineTab()}
          </TabsContent>

          <TabsContent value="heatmap" className="mt-8">
            {renderHeatmapTab()}
          </TabsContent>

          <TabsContent value="predictive" className="mt-8">
            {renderPredictiveTab()}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ContactAnalyticsDashboard; 